import { stripLocalePrefix } from "@/lib/url/localizeUrl"
import { scheduleScrollToAnchor } from "@/lib/scrollToAnchor"

/** Legacy `?tab=` values → section `id` on the page (`pricing-cards-{n}` from renderPage). */
const LEGACY_TAB_TO_SECTION_ID: Record<string, string> = {
  advanced: "pricing-cards-2",
}

/** Pages that used `?tab=` deep links. Matched after stripping the locale prefix. */
const TAB_QUERY_PAGES = new Set(["/plans-and-pricing"])

function normalizePagePath(pathname: string): string {
  let path = stripLocalePrefix(pathname)
  if (!path.startsWith("/")) path = `/${path}`
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1)
  return path
}

/**
 * Resolve a legacy `?tab=` value to a section hash for the given pathname.
 * Used by silent SSO to persist deep links that are still query-based.
 */
export function getLegacyTabHash(
  tab: string | null | undefined,
  pathname: string,
): string | null {
  if (!tab) return null

  const sectionId = LEGACY_TAB_TO_SECTION_ID[tab]
  if (!sectionId) return null

  if (!TAB_QUERY_PAGES.has(normalizePagePath(pathname))) {
    return null
  }

  return `#${sectionId}`
}

/**
 * Rewrite legacy `?tab=` URLs to `#section-id` and scroll. Server redirects
 * cannot deliver hash anchors on the same path, so this runs client-side.
 *
 * Returns true when the URL was migrated (caller should skip scroll-to-top).
 */
export function migrateLegacyTabQueryToHash(): boolean {
  if (typeof window === "undefined") return false

  const params = new URLSearchParams(window.location.search)
  const tab = params.get("tab")
  const hash = getLegacyTabHash(tab, window.location.pathname)
  if (!hash) return false

  params.delete("tab")
  const search = params.toString()
  const cleanUrl = `${window.location.pathname}${search ? `?${search}` : ""}${hash}`

  window.history.replaceState(null, "", cleanUrl)
  scheduleScrollToAnchor(hash)
  return true
}
